import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../lib/apiBase';

/**
 * Shows a banner at the top of the dashboard when the workspace has decisions
 * older than 30 days still in 'new' or 'planned' state — prompt to review.
 */
const StaleDecisionsBanner = ({ workspaceId }) => {
  const navigate = useNavigate();
  const [stale, setStale] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/decisions/stale`, {
          params: { workspace_id: workspaceId, days: 30, limit: 20 },
        });
        setStale(res.data);
      } catch (err) {
        // silently ignore — non-critical UI
      }
    })();
  }, [workspaceId]);

  if (dismissed || !stale || (stale.count || 0) === 0) return null;

  return (
    <div
      className="bg-orange-500/10 border-b border-orange-500/30"
      data-testid="stale-banner"
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
          <div className="text-sm text-foreground">
            <span className="font-medium">{stale.count} decision{stale.count === 1 ? '' : 's'} untouched for 30+ days.</span>
            <span className="text-muted-foreground ml-1">Time to commit, archive, or delete.</span>
          </div>
          <Badge variant="outline" className="border-orange-500/40 text-orange-700 text-[10px]">
            30+ days
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/roadmap')}
            data-testid="stale-banner-review"
          >
            Review
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            data-testid="stale-banner-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StaleDecisionsBanner;
